import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate, useNavigationType } from 'react-router-dom';
import { debounce } from 'lodash';

import { analyticsClickViewCanceledRides, analyticsPage } from '../../api/services/Analytics';
import { MINIMUM_SEARCH_STRING_LENGTH, RIDE_STATES_QUERY } from '../../constants';
import { useTimezone } from '../../hooks/useTimezone';
import { useOptimisticTabSelection } from '../../shared/hooks/useOptimisticTabSelection';

import { readRidesSearchQuery, RIDES_FILTER_URL_KEYS } from './ridesURLParams';

const UPCOMING_STATE = RIDE_STATES_QUERY.upcoming;
const COMPLETED_STATE = RIDE_STATES_QUERY.completed;
const REQUESTED_STATE = RIDE_STATES_QUERY.requested;
const CANCELED_STATES = [RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.billableCanceled];
const CANCELED_TAB: RideStateTab = RIDE_STATES_QUERY.allCanceled as RideStateTab;

const REMOVED_RIDERS_PATH = '/rides/removed_riders';

export const REMOVED_RIDERS_TAB = 'removed_riders';

type RideStateTab = 'all' | 'completed' | 'requested' | 'all_canceled';

export type RidesTab = RideStateTab | typeof REMOVED_RIDERS_TAB;

const isRideStateTab = (tab: string): tab is RideStateTab =>
  [UPCOMING_STATE, COMPLETED_STATE, CANCELED_TAB, REQUESTED_STATE].includes(tab as RideStateTab);

const getRidesTabFromState = (state: string | null): RideStateTab | false => {
  if (state && CANCELED_STATES.includes(state)) {
    return CANCELED_TAB;
  }

  const currentTab = state || UPCOMING_STATE;

  return [UPCOMING_STATE, COMPLETED_STATE, REQUESTED_STATE].includes(currentTab as RideStateTab)
    ? (currentTab as RideStateTab)
    : false;
};

const getTabPageName = (tab: RideStateTab): string => {
  const pageNames: Record<RideStateTab, string> = {
    all: 'In Progress & Upcoming',
    completed: 'Completed',
    all_canceled: 'Canceled',
    requested: 'Requested',
  };

  return pageNames[tab];
};

export const useRidesTabs = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const isRemovedRidersRoute = !!useMatch(REMOVED_RIDERS_PATH);
  const { getStartOfDate, getEndOfDate } = useTimezone();
  const locationRef = useRef(location);

  locationRef.current = location;

  const urlParams = new URLSearchParams(location.search);
  const urlState = urlParams.get('state');
  const committedTab: RidesTab | false = isRemovedRidersRoute ? REMOVED_RIDERS_TAB : getRidesTabFromState(urlState);
  const committedKey = isRemovedRidersRoute ? REMOVED_RIDERS_TAB : `${location.pathname}:${urlState || UPCOMING_STATE}`;
  const {
    displayedValue: selectedTab,
    hasPendingSelection,
    select,
    reset,
  } = useOptimisticTabSelection<RidesTab | false>({ committedValue: committedTab, committedKey });
  const [tabIntentId, setTabIntentId] = useState(0);
  const locationKeyRef = useRef(location.key);
  const committedKeyRef = useRef(committedKey);
  const isCommittingTabRef = useRef(false);

  const commitRegularTab = useMemo(
    () =>
      debounce((nextTab: RideStateTab) => {
        const currentLocation = locationRef.current;
        const params = new URLSearchParams(currentLocation.search);
        const currentState = params.get('state') || UPCOMING_STATE;
        const currentDate = params.has('fromDate') ? params.get('fromDate') : null;
        let nextState: string = nextTab;
        let nextDate: string | null = null;

        if (nextTab === CANCELED_TAB) {
          nextState = RIDE_STATES_QUERY.billableCanceled;
          nextDate = getEndOfDate(new Date()).toISOString();
        } else if (CANCELED_STATES.includes(currentState) || currentState === REQUESTED_STATE) {
          nextDate = null;
        } else if (nextTab === REQUESTED_STATE) {
          nextDate = currentDate ? getStartOfDate(new Date(currentDate)).toISOString() : null;
        } else if (currentDate) {
          nextDate =
            [UPCOMING_STATE, REQUESTED_STATE].indexOf(nextTab) !== -1
              ? getStartOfDate(new Date(currentDate)).toISOString()
              : getEndOfDate(new Date(currentDate)).toISOString();
        }

        params.set('state', nextState);

        const searchQuery = readRidesSearchQuery(params);

        if (searchQuery) {
          params.set('q', searchQuery);
        } else {
          params.delete('q');
        }
        params.delete('query');

        if (nextDate) {
          params.set('fromDate', nextDate);
        } else {
          params.delete('fromDate');
        }

        RIDES_FILTER_URL_KEYS.forEach((key) => params.delete(key));

        const search = params.toString();

        isCommittingTabRef.current = true;
        navigate({ pathname: '/rides', search: search ? `?${search}` : '' });
      }, 400),
    [getEndOfDate, getStartOfDate, navigate]
  );

  const clearPendingSelection = useCallback(() => {
    commitRegularTab.cancel();
    reset();
  }, [commitRegularTab, reset]);

  useEffect(() => {
    const locationChanged = locationKeyRef.current !== location.key;
    const committedTabChanged = committedKeyRef.current !== committedKey;

    if (!locationChanged && !committedTabChanged) {
      return;
    }

    locationKeyRef.current = location.key;
    committedKeyRef.current = committedKey;

    if (navigationType === 'POP' || committedTabChanged) {
      commitRegularTab.cancel();
      reset();
      if (navigationType === 'POP' || !isCommittingTabRef.current) {
        setTabIntentId((current) => current + 1);
      }
      isCommittingTabRef.current = false;
    }
  }, [commitRegularTab, committedKey, location.key, navigationType, reset]);

  useEffect(() => () => commitRegularTab.cancel(), [commitRegularTab]);

  const handleTabChange = useCallback(
    (nextTab: string) => {
      if (isRemovedRidersRoute) {
        commitRegularTab.cancel();

        if (nextTab === REMOVED_RIDERS_TAB) {
          navigate(REMOVED_RIDERS_PATH);

          return;
        }

        if (!isRideStateTab(nextTab)) {
          return;
        }

        const state = nextTab === CANCELED_TAB ? RIDE_STATES_QUERY.billableCanceled : nextTab;

        navigate(`/rides?state=${state}`);

        return;
      }

      if (!isRideStateTab(nextTab) && nextTab !== REMOVED_RIDERS_TAB) {
        return;
      }

      if (nextTab === REMOVED_RIDERS_TAB) {
        commitRegularTab.cancel();
        reset();
        isCommittingTabRef.current = false;
        setTabIntentId((current) => current + 1);
        navigate(REMOVED_RIDERS_PATH);

        return;
      }

      if (nextTab === selectedTab) {
        return;
      }

      if (nextTab === committedTab) {
        commitRegularTab.cancel();
        reset();
        isCommittingTabRef.current = false;
        setTabIntentId((current) => current + 1);

        return;
      }

      const params = new URLSearchParams(location.search);
      const query = readRidesSearchQuery(params);

      if (query && query.length < MINIMUM_SEARCH_STRING_LENGTH) {
        return;
      }

      const rideStateTab = nextTab as RideStateTab;

      select(rideStateTab);
      setTabIntentId((current) => current + 1);
      analyticsPage('Rides', { tab: getTabPageName(rideStateTab) });

      if (rideStateTab === CANCELED_TAB) {
        analyticsClickViewCanceledRides();
      }

      commitRegularTab(rideStateTab);
    },
    [commitRegularTab, committedTab, isRemovedRidersRoute, location.search, navigate, reset, select, selectedTab]
  );

  return {
    selectedTab,
    committedTab,
    committedFromDate: urlParams.get('fromDate'),
    hasPendingSelection,
    tabIntentId,
    clearPendingSelection,
    handleTabChange,
    isRemovedRidersRoute,
  };
};
